/**
 * Phone Mask Utilities for Brazilian Phone Numbers
 *
 * Supports automatic formatting for:
 * - Landline: (XX) XXXX-XXXX (10 digits)
 * - Mobile: (XX) XXXXX-XXXX (11 digits)
 */

/**
 * Remove all non-digit characters from a string
 * @param value - The input string
 * @returns Only the numeric digits
 */
export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Format a string as landline phone (XX) XXXX-XXXX
 * @param value - Numeric string with up to 10 digits
 * @returns Formatted landline phone string
 */
export function formatLandline(value: string): string {
  // Limit to 10 digits
  const digits = value.slice(0, 10);

  // Apply landline mask: (XX) XXXX-XXXX
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')      // Add area code with parentheses
    .replace(/(\d{4})(\d{1,4})$/, '$1-$2'); // Add dash before last 4 digits
}

/**
 * Format a string as mobile phone (XX) XXXXX-XXXX
 * @param value - Numeric string with up to 11 digits
 * @returns Formatted mobile phone string
 */
export function formatMobile(value: string): string {
  // Limit to 11 digits
  const digits = value.slice(0, 11);

  // Apply mobile mask: (XX) XXXXX-XXXX
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')      // Add area code with parentheses
    .replace(/(\d{5})(\d{1,4})$/, '$1-$2'); // Add dash before last 4 digits
}

/**
 * Auto-detect and format as landline or mobile based on length
 * @param value - Input string (may contain non-numeric characters)
 * @returns Formatted phone number
 */
export function formatPhone(value: string): string {
  const digits = onlyDigits(value);

  // If 10 digits or less, format as landline
  if (digits.length <= 10) {
    return formatLandline(digits);
  }

  // Otherwise, format as mobile (11 digits)
  return formatMobile(digits);
}
